import {Locale} from '@dms/api/base';
import {WarrantyTermsAndConditionsRequestBody} from '@dms/api/sales';

/**
 * Builds the per-locale form map (locale code -> text) used by the terms-and-conditions
 * textareas. Every enabled language gets a key (defaulting to an empty string) so a
 * textarea renders for it; existing texts from a loaded program are filled in.
 *
 * Entries for locales that are no longer in supportedLocales are preserved as-is so that
 * a PUT update (which replaces the entire array) does not silently drop texts the tenant
 * saved while that locale was still active.
 */
export const buildTermsAndConditionsRecord = (
  supportedLocales: Locale[],
  termsAndConditions?: WarrantyTermsAndConditionsRequestBody[]
): Record<string, string> => {
  const savedEntries = termsAndConditions ?? [];
  const textByLocale = new Map<string, string>(
    savedEntries.map((item) => [item.locale, item.text])
  );

  const supportedLocalesSet = new Set<string>(supportedLocales);

  const orphanedEntries = savedEntries
    .filter((item) => !supportedLocalesSet.has(item.locale))
    .map((item) => [item.locale, item.text]);

  const supportedEntries = supportedLocales.map((locale) => [
    locale,
    textByLocale.get(locale) ?? '',
  ]);

  return Object.fromEntries([...orphanedEntries, ...supportedEntries]);
};
